import React, { useState } from 'react';
import {
    Button, Box, Typography, Chip, Collapse, IconButton, Tooltip, Card, FormControlLabel, Switch
} from "@mui/material";
import {
    ExpandMore as ExpandMoreIcon, ExpandLess as ExpandLessIcon, Clear as ClearIcon, Edit as EditIcon,
    Map as MapIcon, Label as LabelIcon, Stop as StopIcon, Language as LanguageIcon
} from '@mui/icons-material';
import {PLACE_TYPES, PLACE_TYPE_PRESETS} from '../models/PlaceType';
import LanguageSelector from './LanguageSelector';
import PlaceTypesDialog from './PlaceTypesDialog';

const SearchControls = ({
    selectedPlaceTypes,
    onPlaceTypeChange,
    onSearchClick,
    onStopQuery,
    isLoading,
    selectedLanguage,
    customLanguageObject,
    languages,
    customLanguage,
    customLanguageCode,
    onLanguageChange,
    onCustomLanguageChange,
    onCustomLanguageCodeChange,
    onOpenLanguagePanel,
    includeLabeled,
    onIncludeLabeledChange
}) => {
    const [showPlaceTypesDialog, setShowPlaceTypesDialog] = useState(false);
    const [languageSectionExpanded, setLanguageSectionExpanded] = useState(false);
    const [geoFeaturesSectionExpanded, setGeoFeaturesSectionExpanded] = useState(false);
    
    const getLanguageDisplayText = () => {
        if (customLanguageObject) {
            return `${customLanguageObject.customLabel || 'Custom'} (${customLanguageObject.customLanguageCode || ''})`;
        }
        if (selectedLanguage) {
            return selectedLanguage.wikidataLabel || 'Selected Language';
        }
        return 'Select Language';
    };



    const isLanguageSelected = selectedLanguage || customLanguageObject;
    const hasValidLanguage = selectedLanguage?.ietfLanguageTag || 
                           (customLanguageObject?.customLabel && customLanguageObject?.customLanguageCode);

    return (
        <Box>
            {/* Language Selection Card */}
            <Card sx={{ 
                mb: 2,
                border: '1px solid rgba(226, 232, 240, 0.8)',
                borderRadius: 2,
                overflow: 'hidden',
                transition: 'all 0.2s ease-in-out',
                '&:hover': {
                    borderColor: 'primary.main',
                    boxShadow: '0 2px 8px rgba(37, 99, 235, 0.1)'
                }
            }}>
                {/* Language Section Header */}
                <Box sx={{ 
                    display: 'flex', 
                    justifyContent: 'space-between', 
                    alignItems: 'center', 
                    p: 2,
                    borderBottom: languageSectionExpanded ? '1px solid rgba(226, 232, 240, 0.5)' : 'none',
                    cursor: 'pointer',
                    '&:hover': {
                        backgroundColor: 'rgba(59, 130, 246, 0.05)'
                    },
                    transition: 'all 0.2s ease-in-out'
                }}
                onClick={() => setLanguageSectionExpanded(!languageSectionExpanded)}
                >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                        <LanguageIcon sx={{ color: 'primary.main' }} />
                        <Typography variant="h6" sx={{ fontWeight: 600 }}>
                            Language Selection
                        </Typography>
                        {isLanguageSelected && (
                            <Chip
                                label={getLanguageDisplayText()}
                                color="primary"
                                size="small"
                                sx={{
                                    maxWidth: '160px',
                                    '& .MuiChip-label': {
                                        fontSize: '0.7rem'
                                    }
                                }}
                            />
                        )}
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        {!isLanguageSelected && (
                            <Typography variant="caption" color="warning.main" sx={{ fontWeight: 500 }}>
                                Required
                            </Typography>
                        )}
                        <IconButton 
                            size="small"
                            sx={{ 
                                transform: languageSectionExpanded ? 'rotate(0deg)' : 'rotate(-90deg)',
                                transition: 'transform 0.3s ease-in-out'
                            }}
                        >
                            {languageSectionExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                        </IconButton>
                    </Box>
                </Box>

                {/* Language Section Content */}
                <Collapse in={languageSectionExpanded} timeout={300}>
                    <Box sx={{ p: 2 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                            <Typography variant="body2" color="text.secondary">
                                {isLanguageSelected ? 'Current selection:' : 'No language selected yet'}
                            </Typography>
                            <Button
                                variant="outlined"
                                size="small"
                                startIcon={<EditIcon />}
                                onClick={() => {
                                    if (onOpenLanguagePanel) {
                                        onOpenLanguagePanel();
                                    }
                                }}
                                sx={{
                                    fontSize: '0.7rem',
                                    py: 0.5,
                                    px: 1.5,
                                    textTransform: 'none',
                                    borderRadius: 2
                                }}
                            >
                                {isLanguageSelected ? 'Change' : 'Select'}
                            </Button>
                        </Box>
                        
                        {isLanguageSelected ? (
                            <Box sx={{
                                p: 2,
                                backgroundColor: 'rgba(59, 130, 246, 0.08)',
                                borderRadius: 2,
                                border: '1px solid rgba(59, 130, 246, 0.2)'
                            }}>
                                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                                    {getLanguageDisplayText()}
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                    You can change this selection anytime during your session
                                </Typography>
                            </Box>
                        ) : (
                            <Box sx={{
                                p: 2,
                                backgroundColor: 'rgba(239, 68, 68, 0.08)',
                                borderRadius: 2,
                                border: '1px solid rgba(239, 68, 68, 0.2)'
                            }}>
                                <Typography variant="body2" sx={{ fontWeight: 500, color: 'error.main' }}>
                                    Language selection is required to search for places
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                    Click "Select" to choose your native language
                                </Typography>
                            </Box>
                        )}
                    </Box>
                </Collapse>
            </Card>

            {/* Include Already Labeled Places Toggle */}
            {isLanguageSelected && (
                <Card sx={{ 
                    mb: 2,
                    border: '1px solid rgba(226, 232, 240, 0.8)',
                    borderRadius: 2,
                    overflow: 'hidden'
                }}>
                    <Box sx={{ p: 2 }}>
                        <FormControlLabel
                            control={
                                <Switch
                                    checked={includeLabeled}
                                    onChange={onIncludeLabeledChange}
                                    color="primary"
                                />
                            }
                            label={
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <LabelIcon sx={{ color: 'primary.main', fontSize: 20 }} />
                                    <Box>
                                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                            Include places with existing labels
                                        </Typography>
                                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                                            Show places that already have names in {getLanguageDisplayText()} for editing/improvement
                                        </Typography>
                                    </Box>
                                </Box>
                            }
                            sx={{ 
                                m: 0,
                                width: '100%',
                                justifyContent: 'space-between',
                                '& .MuiFormControlLabel-label': { 
                                    flex: 1 
                                }
                            }}
                        />
                    </Box>
                </Card>
            )}

            {/* Geographic Features Selection Card */}
            <Card sx={{ 
                mb: 2,
                border: '1px solid rgba(226, 232, 240, 0.8)',
                borderRadius: 2,
                overflow: 'hidden',
                transition: 'all 0.2s ease-in-out',
                '&:hover': {
                    borderColor: 'primary.main',
                    boxShadow: '0 2px 8px rgba(37, 99, 235, 0.1)'
                }
            }}>
                {/* Geographic Features Section Header */}
                <Box sx={{ 
                    display: 'flex', 
                    justifyContent: 'space-between', 
                    alignItems: 'center', 
                    p: 2,
                    borderBottom: geoFeaturesSectionExpanded ? '1px solid rgba(226, 232, 240, 0.5)' : 'none',
                    cursor: 'pointer',
                    '&:hover': {
                        backgroundColor: 'rgba(59, 130, 246, 0.05)'
                    },
                    transition: 'all 0.2s ease-in-out'
                }}
                onClick={() => setGeoFeaturesSectionExpanded(!geoFeaturesSectionExpanded)}
                >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                        <MapIcon sx={{ color: 'primary.main' }} />
                        <Typography variant="h6" sx={{ fontWeight: 600 }}>
                            Geographic Features ({selectedPlaceTypes.length})
                        </Typography>
                        {selectedPlaceTypes.length > 0 && (
                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                {selectedPlaceTypes.slice(0, 2).map((typeId) => {
                                    const type = PLACE_TYPES.find(t => t.id === typeId);
                                    return (
                                        <Chip
                                            key={typeId}
                                            icon={<span style={{ fontSize: '0.6rem' }}>{type?.icon}</span>}
                                            label={type?.name || typeId}
                                            size="small"
                                            variant="outlined"
                                            color="primary"
                                            sx={{ fontSize: '0.65rem', height: '20px' }}
                                        />
                                    );
                                })}
                                {selectedPlaceTypes.length > 2 && (
                                    <Chip
                                        label={`+${selectedPlaceTypes.length - 2}`}
                                        size="small"
                                        variant="outlined"
                                        sx={{ fontSize: '0.65rem', height: '20px' }}
                                    />
                                )}
                            </Box>
                        )}
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        {selectedPlaceTypes.length === 0 && (
                            <Typography variant="caption" color="warning.main" sx={{ fontWeight: 500 }}>
                                Required
                            </Typography>
                        )}
                        <IconButton 
                            size="small"
                            sx={{ 
                                transform: geoFeaturesSectionExpanded ? 'rotate(0deg)' : 'rotate(-90deg)',
                                transition: 'transform 0.3s ease-in-out'
                            }}
                        >
                            {geoFeaturesSectionExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                        </IconButton>
                    </Box>
                </Box>

                {/* Geographic Features Section Content */}
                <Collapse in={geoFeaturesSectionExpanded} timeout={300}>
                    <Box sx={{ p: 2 }}>
                        {/* Show selected types summary */}
                        <Box sx={{ mb: 2 }}>
                            {selectedPlaceTypes.length > 0 ? (
                                <Box>
                                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                                        Selected features:
                                    </Typography>
                                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                        {selectedPlaceTypes.map((typeId) => {
                                            const type = PLACE_TYPES.find(t => t.id === typeId);
                                            return (
                                                <Chip
                                                    key={typeId}
                                                    icon={<span style={{ fontSize: '0.7rem' }}>{type?.icon}</span>}
                                                    label={type?.name || typeId}
                                                    size="small"
                                                    variant="filled"
                                                    color="primary"
                                                    onDelete={() => {
                                                        const newSelection = selectedPlaceTypes.filter(id => id !== typeId);
                                                        onPlaceTypeChange(newSelection);
                                                    }}
                                                    sx={{ fontSize: '0.7rem', height: '24px' }}
                                                />
                                            );
                                        })}
                                    </Box>
                                </Box>
                            ) : (
                                <Box sx={{
                                    p: 2,
                                    backgroundColor: 'rgba(239, 68, 68, 0.08)',
                                    borderRadius: 2,
                                    border: '1px solid rgba(239, 68, 68, 0.2)',
                                    mb: 2
                                }}>
                                    <Typography variant="body2" sx={{ fontWeight: 500, color: 'error.main' }}>
                                        No geographic features selected
                                    </Typography>
                                    <Typography variant="caption" color="text.secondary">
                                        Choose at least one feature type to search for places
                                    </Typography>
                                </Box>
                            )}
                        </Box>

                        {/* Quick preset buttons */}
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                            Quick selections:
                        </Typography>
                        <Box sx={{ display: 'flex', gap: 1, mb: 2, flexWrap: 'wrap' }}>
                            {Object.entries(PLACE_TYPE_PRESETS).map(([presetKey, preset]) => (
                                <Tooltip key={presetKey} title={`Select: ${preset.types.join(', ')}`}>
                                    <Button
                                        size="small"
                                        variant="outlined"
                                        startIcon={<span style={{ fontSize: '0.8rem' }}>{preset.icon}</span>}
                                        onClick={() => onPlaceTypeChange(preset.types)}
                                        sx={{ 
                                            fontSize: '0.7rem', 
                                            py: 0.5,
                                            textTransform: 'none',
                                            borderRadius: 2
                                        }}
                                    >
                                        {preset.name}
                                    </Button>
                                </Tooltip>
                            ))}
                            <Button
                                size="small"
                                variant="outlined"
                                startIcon={<ClearIcon />}
                                onClick={() => onPlaceTypeChange([])}
                                sx={{ fontSize: '0.7rem', py: 0.5, borderRadius: 2 }}
                                color="secondary"
                            >
                                Clear All
                            </Button>
                        </Box>

                        {/* Detailed Selection Button */}
                        <Button
                            variant="contained"
                            onClick={() => setShowPlaceTypesDialog(true)}
                            fullWidth
                            sx={{
                                textTransform: 'none',
                                borderRadius: 2,
                                py: 1,
                                fontWeight: 600
                            }}
                        >
                            Browse All Geographic Features
                        </Button>
                    </Box>
                </Collapse>
            </Card>

            {/* Search Button - Always Visible and Sticky */}
            <Box sx={{ 
                position: 'sticky',
                bottom: 0,
                zIndex: 1100,
                mt: 2,
                pt: 2,
                background: 'linear-gradient(to bottom, transparent 0%, rgba(248, 250, 252, 0.95) 20%, rgba(248, 250, 252, 0.98) 100%)',
                backdropFilter: 'blur(8px)',
                borderRadius: '0 0 16px 16px',
                mx: -2,
                px: 2,
                pb: 1
            }}>
                <Box sx={{ display: 'flex', gap: 1 }}>
                    <Button
                        variant="contained"
                        color="primary"
                        onClick={onSearchClick}
                        disabled={isLoading || selectedPlaceTypes.length === 0 || !hasValidLanguage}
                        size="large"
                        sx={{
                            flex: 1,
                            py: 1.5,
                            fontSize: '1rem',
                            fontWeight: 600,
                            borderRadius: 3,
                            textTransform: 'none',
                            boxShadow: '0 4px 12px rgba(37, 99, 235, 0.3)',
                            '&:hover': {
                                boxShadow: '0 6px 16px rgba(37, 99, 235, 0.4)',
                                transform: 'translateY(-1px)'
                            },
                            '&:disabled': {
                                opacity: 0.6,
                                boxShadow: 'none',
                                transform: 'none'
                            },
                            transition: 'all 0.2s ease-in-out'
                        }}
                    >
                        {isLoading ? 'Searching...' : 
                         !hasValidLanguage ? 'Select Language First' :
                         selectedPlaceTypes.length === 0 ? 'Select Place Types' : 
                         'Search Places'}
                    </Button>
                    
                    {isLoading && (
                        <Button
                            variant="contained"
                            color="error"
                            onClick={onStopQuery}
                            size="large"
                            sx={{
                                minWidth: '120px',
                                py: 1.5,
                                fontSize: '1rem',
                                fontWeight: 600,
                                borderRadius: 3,
                                textTransform: 'none',
                                boxShadow: '0 4px 12px rgba(239, 68, 68, 0.3)',
                                '&:hover': {
                                    boxShadow: '0 6px 16px rgba(239, 68, 68, 0.4)',
                                    transform: 'translateY(-1px)'
                                },
                                transition: 'all 0.2s ease-in-out'
                            }}
                        >
                            <StopIcon sx={{ mr: 1 }} />
                            Stop
                        </Button>
                    )}
                </Box>
            </Box>


            {/* Place Types Dialog */}
            <PlaceTypesDialog
                open={showPlaceTypesDialog}
                onClose={() => setShowPlaceTypesDialog(false)}
                selectedPlaceTypes={selectedPlaceTypes}
                onPlaceTypeChange={onPlaceTypeChange}
            />
        </Box>
    );
};

// Note: selectedPlaceTypes should be an array of place type IDs (e.g., ['Q515', 'Q3957', 'Q532'])
// These correspond to Wikidata Q-IDs for different place types

export default SearchControls;